import { useState, useCallback } from "react";
import Checkbox from "../checkbox/Checkbox";
import QuestionRenderer from "../questionRenderer/QuestionRenderer";
import { usePermissions } from "../../hooks/usePermissions";
import { useResources } from "../../hooks/useResources";
import type { QuestionMetadata } from "../../interfaces/componentInterfaces/questionRenderer";
import "./QuestionCard.scss";

interface QuestionCardProps {
  id: string;
  questionNumber: number;
  status?: "Published" | "Unpublished";
  jobId?: string;
  isSelected?: boolean;
  showCheckbox?: boolean;
  onSelect?: (id: string, selected: boolean) => void;
  onPublish?: (id: string) => void;
}

const QuestionCard = ({
  id,
  questionNumber,
  status = "Unpublished",
  jobId,
  isSelected = false,
  showCheckbox = true,
  onSelect,
  onPublish,
}: QuestionCardProps) => {
  const { canPublishQuestions } = usePermissions();
  const { getResourceValue } = useResources();
  const [metadata, setMetadata] = useState<QuestionMetadata | null>(null);
  
  const handleCheckboxChange = (checked: boolean) => {
    if (onSelect) {
      onSelect(id, checked);
    }
  };

  const handleMetadataLoaded = useCallback((meta: QuestionMetadata) => {
    setMetadata(meta);
  }, []);

  return (
    <div
      className={`question-card ${
        !showCheckbox ? "question-card--no-checkbox" : ""
      }`}
    >
      {showCheckbox && (
        <Checkbox
          id={`question-${id}`}
          checked={isSelected}
          onChange={handleCheckboxChange}
          disabled={status === "Published"}
        />
      )}
      <div className="question-card__content">
        <div className="question-card__header">
          <h3 className="question-card__title">
            Question {questionNumber}{" "}
            <span className="question-card__id">({id})</span>
          </h3>
          {status === "Unpublished" ? (
            canPublishQuestions ? (
              <button
                className="question-card__publish-btn"
                onClick={() => onPublish && onPublish(id)}
              >
                <i className="f-icon upload-icon"></i>
                <span>Publish</span>
              </button>
            ) : (
              <span className="question-card__status question-card__status--unpublished">
                {status}
              </span>
            )
          ) : (
            <span className="question-card__status question-card__status--published">
              {status}
            </span>
          )}
        </div>
        {metadata && (
          <div className="question-card__metadata">
            <div className="question-card__metadata-field">
              <span className="question-card__metadata-label">
                {getResourceValue("parent_id", "Parent ID")}
              </span>
              <span className="question-card__metadata-value">
                {metadata.parentId || "—"}
              </span>
            </div>
            <div className="question-card__metadata-field">
              <span className="question-card__metadata-label">
                {getResourceValue("section_id", "Section ID")}
              </span>
              <span className="question-card__metadata-value">
                {metadata.sectionId || "—"}
              </span>
            </div>
            <div className="question-card__metadata-field">
              <span className="question-card__metadata-label">
                {getResourceValue("country", "Country")}
              </span>
              <span className="question-card__metadata-value">
                {metadata.country || "—"}
              </span>
            </div>
            <div className="question-card__metadata-field">
              <span className="question-card__metadata-label">
                {getResourceValue("subject", "Subject")}
              </span>
              <span className="question-card__metadata-value">
                {metadata.subject || "—"}
              </span>
            </div>
            <div className="question-card__metadata-field">
              <span className="question-card__metadata-label">
                {getResourceValue("grade", "Grade")}
              </span>
              <span className="question-card__metadata-value">
                {metadata.grade || "—"}
              </span>
            </div>
            <div className="question-card__metadata-field">
              <span className="question-card__metadata-label">
                {getResourceValue("language", "Language")}
              </span>
              <span className="question-card__metadata-value">
                {metadata.language || "—"}
              </span>
            </div>
            <div className="question-card__metadata-field">
              <span className="question-card__metadata-label">
                {getResourceValue("question_type", "Question Type")}
              </span>
              <span className="question-card__metadata-value">
                {metadata.questionTypes || "—"}
              </span>
            </div>
          </div>
        )}
        <div className="question-card__wrapper">
          <QuestionRenderer
            questionId={id}
            jobId={jobId}
            onMetadataLoaded={handleMetadataLoaded}
          />
        </div>
      </div>
    </div>
  );
};
export default QuestionCard;
